// src/contexts/CatylexCitationTooltipContext.tsx

import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { track } from "../utils/analytics";
import { CatylexConcept } from "../interfaces/documentCitationTypes";
import type { CitationTooltipGroup } from "./citationTooltipGroup";
import { wrapIndex } from "./citationTooltipGroup";

/** What the tooltip shows for one Catylex citation. */
export interface CatylexCitationTooltipData {
  documentTitle: string;
  chunkType: "contract" | "sentence";
  breadcrumb: string[];
  citedText: string;
  atrUrl?: string;
  contractStatus?: "Active" | "Inactive";
  pages?: number[];
  concepts?: CatylexConcept[];
}

interface CatylexCitationTooltipState extends CatylexCitationTooltipData {
  isVisible: boolean;
  anchorRect: DOMRect | null;
  /** Set when a merged chip is shown; null for a single citation. */
  group: CitationTooltipGroup<CatylexCitationTooltipData> | null;
  /** Opened by click/Enter on a merged chip: ignores mouse leave until closed. */
  pinned: boolean;
}

interface CatylexCitationTooltipContextType {
  state: CatylexCitationTooltipState;
  showTooltip: (data: CatylexCitationTooltipData & { anchorRect: DOMRect | null }) => void;
  /** Show a merged chip's citations, paged. Re-showing the same chip keeps its page. */
  showTooltipGroup: (
    anchorEl: HTMLElement,
    items: CatylexCitationTooltipData[],
    pinned: boolean
  ) => void;
  /** Move the merged chip's tooltip by `delta` citations, wrapping around. */
  pageTooltip: (delta: number) => void;
  /** Hide now, pinned or not. */
  closeTooltip: () => void;
  hideTooltip: () => void;
  /** Keep tooltip visible when mouse enters tooltip area */
  handleTooltipMouseEnter: () => void;
  /** Hide tooltip when mouse leaves tooltip area */
  handleTooltipMouseLeave: () => void;
}

const defaultState: CatylexCitationTooltipState = {
  isVisible: false,
  anchorRect: null,
  documentTitle: "",
  chunkType: "contract",
  breadcrumb: [],
  citedText: "",
  group: null,
  pinned: false,
};

const CatylexCitationTooltipContext = createContext<CatylexCitationTooltipContextType | undefined>(
  undefined
);

interface CatylexCitationTooltipProviderProps {
  children: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

/**
 * Provider for catylex citation tooltip state. Sibling of
 * PlaybookCitationTooltipProvider and DocumentCitationTooltipProvider — all
 * three mount once at the chat root so tooltips never unmount during streaming.
 */
export const CatylexCitationTooltipProvider: React.FC<CatylexCitationTooltipProviderProps> = ({
  children,
  scrollContainerRef,
}) => {
  const [state, setState] = useState<CatylexCitationTooltipState>(defaultState);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const analyticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleRef = useRef(false);
  const pinnedRef = useRef(false);
  const groupRef = useRef<CitationTooltipGroup<CatylexCitationTooltipData> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const clearAnalyticsTimeout = useCallback(() => {
    if (analyticsTimeoutRef.current) {
      clearTimeout(analyticsTimeoutRef.current);
      analyticsTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(
    (data: CatylexCitationTooltipData & { anchorRect: DOMRect | null }) => {
      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = true;
      pinnedRef.current = false;
      groupRef.current = null;
      setState({ isVisible: true, ...data, group: null, pinned: false });
      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "catylex",
          document_title: data.documentTitle,
          chunk_type: data.chunkType,
          breadcrumb: data.breadcrumb.join(" › "),
          concept_count: data.concepts?.length ?? 0,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearHideTimeout, clearAnalyticsTimeout]
  );

  // One view per page a reader dwells on, tagged with its place in the group.
  const trackGroupPageViewed = useCallback(
    (group: CitationTooltipGroup<CatylexCitationTooltipData>) => {
      clearAnalyticsTimeout();
      const item = group.items[group.index];

      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "catylex",
          document_title: item.documentTitle,
          chunk_type: item.chunkType,
          breadcrumb: item.breadcrumb.join(" › "),
          concept_count: item.concepts?.length ?? 0,
          group_size: group.items.length,
          group_index: group.index,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearAnalyticsTimeout]
  );

  const showTooltipGroup = useCallback(
    (anchorEl: HTMLElement, items: CatylexCitationTooltipData[], pinned: boolean) => {
      if (items.length === 0) return;
      clearHideTimeout();
      const current = groupRef.current;
      const sameChip = isVisibleRef.current && current?.anchorEl === anchorEl;
      const group = {
        items,
        index: sameChip && current ? Math.min(current.index, items.length - 1) : 0,
        anchorEl,
      };
      const nextPinned = pinned || (sameChip && pinnedRef.current);

      isVisibleRef.current = true;
      pinnedRef.current = nextPinned;
      groupRef.current = group;
      setState({
        isVisible: true,
        anchorRect: anchorEl.getBoundingClientRect(),
        ...items[group.index],
        group,
        pinned: nextPinned,
      });
      if (!sameChip) trackGroupPageViewed(group);
    },
    [clearHideTimeout, trackGroupPageViewed]
  );

  const pageTooltip = useCallback(
    (delta: number) => {
      const current = groupRef.current;

      if (!current || !isVisibleRef.current) return;
      const group = { ...current, index: wrapIndex(current.index + delta, current.items.length) };

      groupRef.current = group;
      // Spread over defaults so optional fields of the previous page don't linger
      setState((prev) => ({
        ...prev,
        atrUrl: undefined,
        contractStatus: undefined,
        pages: undefined,
        concepts: undefined,
        ...group.items[group.index],
        group,
      }));
      trackGroupPageViewed(group);
    },
    [trackGroupPageViewed]
  );

  const closeTooltip = useCallback(() => {
    clearHideTimeout();
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    pinnedRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
  }, [clearHideTimeout, clearAnalyticsTimeout]);

  const hideTooltip = useCallback(() => {
    if (pinnedRef.current) return;
    clearAnalyticsTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      isVisibleRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false }));
    }, 100);
  }, [clearAnalyticsTimeout]);

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    if (pinnedRef.current) return;
    // Clear any pending `hideTooltip` timer before flipping state — otherwise
    // the deferred timeout fires after we've already hidden and produces a
    // spurious re-render with a fresh state-object identity.
    clearHideTimeout();
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false }));
  }, [clearHideTimeout, clearAnalyticsTimeout]);

  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      clearAnalyticsTimeout();
    };
  }, [clearAnalyticsTimeout]);

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!isVisibleRef.current) return;
      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = false;
      pinnedRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef, clearHideTimeout, clearAnalyticsTimeout]);

  return (
    <CatylexCitationTooltipContext.Provider
      value={{
        state,
        showTooltip,
        showTooltipGroup,
        pageTooltip,
        closeTooltip,
        hideTooltip,
        handleTooltipMouseEnter,
        handleTooltipMouseLeave,
      }}
    >
      {children}
    </CatylexCitationTooltipContext.Provider>
  );
};

export const useCatylexCitationTooltip = (): CatylexCitationTooltipContextType => {
  const context = useContext(CatylexCitationTooltipContext);
  if (!context) {
    throw new Error("useCatylexCitationTooltip must be used within CatylexCitationTooltipProvider");
  }
  return context;
};

export default CatylexCitationTooltipContext;
